import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ProductProvider } from './context/ProductContext';
import { QuoteProvider } from './context/QuoteContext';
import { SiteImagesProvider } from './context/SiteImagesContext';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { QuoteDrawer } from './components/QuoteDrawer';
import { ErrorBoundary } from './components/ErrorBoundary';
import { HomePage } from './pages/HomePage';
import { ProductsPage } from './pages/ProductsPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { ServicesPage } from './pages/ServicesPage';
import { CalculatorPage } from './pages/CalculatorPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { AdminLoginPage, checkIsAdminAuthenticated } from './pages/AdminLoginPage';
import { BlogPage } from './pages/BlogPage';
import { BlogDetailPage } from './pages/BlogDetailPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { LegalPage } from './pages/LegalPage';
import { resolveCurrentRoute, buildCleanPath, RouteState } from './utils/navigation';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export default function App() {
  const previousAnalyticsPath = useRef<string | null>(null);
  const [route, setRoute] = useState<RouteState>(() => {
    try {
      return resolveCurrentRoute();
    } catch (e) {
      console.warn('Failed to resolve initial route, defaulting to home', e);
      return { page: 'home' };
    }
  });

  // Handle browser back/forward and clean URL migration safely inside iframe / sandboxed environments
  useEffect(() => {
    // 1. If user loaded with a legacy hash like /#products or /#product/xxx or /#blog/xxx, migrate to clean URL
    if (typeof window !== 'undefined' && window.location?.hash) {
      try {
        const initial = resolveCurrentRoute();
        const cleanPath = buildCleanPath(initial.page, initial.productId || initial.blogSlug);
        window.history.replaceState({}, '', cleanPath);
        setRoute(initial);
      } catch (err) {
        console.warn('Safe history replaceState ignored:', err);
      }
    }

    // 2. Listen to browser popstate (Back / Forward buttons)
    const handlePopState = () => {
      try {
        setRoute(resolveCurrentRoute());
      } catch (err) {
        console.warn('Safe popstate resolution error:', err);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleNavigate = useCallback((page: string, param?: string) => {
    let productId: string | undefined;
    let blogSlug: string | undefined;

    if (page === 'product-detail') {
      productId = param;
    } else if (page === 'blog-detail') {
      blogSlug = param;
    }

    const cleanPath = buildCleanPath(page, param);
    // Push new clean state safely without throwing in restrictive sandboxes
    try {
      window.history.pushState({}, '', cleanPath);
    } catch (err) {
      console.warn('Safe history pushState ignored:', err);
    }

    setRoute({ page, productId, blogSlug });

    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    const pagePath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (previousAnalyticsPath.current === pagePath) return;

    const isInitialPage = previousAnalyticsPath.current === null;
    previousAnalyticsPath.current = pagePath;
    if (!isInitialPage) {
      window.gtag?.('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: pagePath
      });
    }
  }, [route]);

  // Safe page renderer with guaranteed fallback to HomePage to eliminate any possibility of a blank screen
  const renderCurrentPage = () => {
    switch (route.page) {
      case 'products':
        return <ProductsPage onNavigate={handleNavigate} />;
      case 'product-detail':
        return (
          <ProductDetailPage
            productId={route.productId || 'milk-pasteurizer-300l'}
            onNavigate={handleNavigate}
          />
        );
      case 'services':
        return <ServicesPage onNavigate={handleNavigate} />;
      case 'calculator':
        return <CalculatorPage onNavigate={handleNavigate} />;
      case 'blog':
        return <BlogPage onNavigate={handleNavigate} />;
      case 'blog-detail':
        return (
          <BlogDetailPage
            slug={route.blogSlug || route.productId || 'revolutionizing-convenience-transforming-kenyan-markets'}
            onNavigate={handleNavigate}
          />
        );
      case 'about':
        return <AboutPage onNavigate={handleNavigate} />;
      case 'contact':
        return <ContactPage onNavigate={handleNavigate} />;
      case 'privacy-policy':
        return <LegalPage type="privacy-policy" onNavigate={handleNavigate} />;
      case 'terms':
        return <LegalPage type="terms" onNavigate={handleNavigate} />;
      case 'disclaimer':
        return <LegalPage type="disclaimer" onNavigate={handleNavigate} />;
      case 'login':
        return (
          <AdminLoginPage
            onNavigate={handleNavigate}
            onLoginSuccess={() => handleNavigate('admin')}
          />
        );
      case 'admin':
        if (!checkIsAdminAuthenticated()) {
          return (
            <AdminLoginPage
              onNavigate={handleNavigate}
              onLoginSuccess={() => handleNavigate('admin')}
            />
          );
        }
        return <AdminDashboardPage onNavigate={handleNavigate} />;
      case '404':
        return <NotFoundPage onNavigate={handleNavigate} />;
      case 'home':
      default:
        return <HomePage onNavigate={handleNavigate} />;
    }
  };

  const isConsolePage = route.page === 'admin' || route.page === 'login';

  return (
    <ErrorBoundary>
      <ProductProvider>
        <QuoteProvider>
          <SiteImagesProvider>
            <div className="min-h-screen flex flex-col bg-slate-50 text-slate-800 antialiased selection:bg-sky-500 selection:text-white">
            {/* Top Bar Navigation (Hidden in dedicated Admin Console & Login) */}
            {!isConsolePage && <Navbar currentPage={route.page} onNavigate={handleNavigate} />}

            {/* Dynamic Multi-Page Clean Router View */}
            <main className="flex-1 flex flex-col">
              {renderCurrentPage()}
            </main>

            {/* Quotation Basket Drawer */}
            <QuoteDrawer />

            {/* Site Footer (Hidden in dedicated Admin Console & Login) */}
            {!isConsolePage && <Footer onNavigate={handleNavigate} />}
            </div>
          </SiteImagesProvider>
        </QuoteProvider>
      </ProductProvider>
    </ErrorBoundary>
  );
}
